import { computedFrom, observable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { activationStrategy, Router } from "aurelia-router";
import { BindingSignaler } from "aurelia-templating-resources";
import { Claims } from "../../../../constants/authentication/claims";
import {
    BaseMepFilterableCustomGrdPage, BaseMepFilterablePage, BaseMepFilterViewModel, MepCustomer, MepHomeTabsEnum,
    MepLookupItem, MepPortMenuItem, MepTabsConstants, MepTabsEnum, MepEventMessages
} from "../../../../models/mep/mep.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { MepLookupService } from "../../../../services/mep/mep-lookup.service";
import { MepService } from "../../../../services/mep/mep.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { MepAbout } from "./tabs/mep-home/tabs/mep-about/mep-about";
import { MepTerminology } from "./tabs/mep-home/tabs/mep-terminology/mep-terminology";
import { MepDashboard } from "./tabs/mep-dashboard/mep-dashboard";
import { MepEmissionTargets } from "./tabs/mep-emission-targets/mep-emission-targets";
import { MepInsights } from "./tabs/mep-insights/mep-insights";
import { MepVessels } from "./tabs/mep-vessels/mep-vessels";
import { MepYearOverYearComparison } from "./tabs/mep-year-over-year-comparison/mep-year-over-year-comparison";
import { Lookups } from "../../../../constants/mep/mep-lookup.constants";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { CompMonthPicker, MonthYearType } from "../../../components/comp-month-picker/comp-month-picker";
import { PartialTabbedEntityItem } from "@rightship-tech/common-ui/partials/partial-tabbed-entity-item/partial-tabbed-entity-item";
import { MepScenarioPlanning } from "./tabs/mep-scenario-planning/mep-scenario-planning";
import { MepPortsSummary } from "./tabs/mep-home/tabs/mep-ports-summary/mep-ports-summary";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { type TabModel } from "@rightship-tech/common-ui/models/basic.models";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../services/basics/signal-functions";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import moment from "moment";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

type MepTabModel = TabModel & {
    showPorts?: boolean;
};

@autoinject()
@transient()
export class Mep {

    private _isSettingCustomerOnLoad: boolean = false;
    private _isCustomerChange: boolean = false;
    private _subscriptions: Subscription[] = [];
    private _changeTabModel: any;
    private _generatingData: boolean;

    public contactSalesEmailSent: boolean = false;
    public showDialog: boolean = false;
    public mepLastVisitedTabCacheKey: string = "mep-last-visited-tab";
    public months: MonthYearType[] = [];
    public compMonthPickerVm: CompMonthPicker;
    public ports: MepPortMenuItem[];
    public years: MepLookupItem[];
    public customers: MepCustomer[];
    public tabs: MepTabModel[] = [];
    public isRightShipUser: boolean;
    public selectedMonths: MonthYearType[] = [];
    public filterViewModel: BaseMepFilterViewModel;
    public canDownloadEmissionsData: boolean;
    public isMepTrialExpired: boolean;

    /* Currently displayed tab */
    public currentTab: MepTabModel = null;
    public entityItem: PartialTabbedEntityItem;
    //used when step is set in url/ routed to specific step
    public initialStep: string;

    @observable()
    public selectedYear: number;

    @observable()
    public customerId: number;

    @observable()
    public portId: number;

    public showPorts: boolean = true;

    @computedFrom('portId')
    get selectedPort() {
        if (this.ports) {
            return this.ports.filter(x => x.portId == this.portId)[0];
        }
        return null;
    }

    @computedFrom('currentTab')
    get portsSummerySelected() {
        if (this.currentTab?.vm == this.portsSummary && this.currentTab?.step == MepHomeTabsEnum.PortsSummary) {
            return true;
        }
    }

    @computedFrom('currentTab')
    get homeTabSelected() {
        return this.currentTab?.vm == this.portsSummary || this.currentTab?.vm == this.portAbout || this.currentTab?.vm == this.portTerminology;
    }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    constructor(private mepService: MepService, private userClientService: UserClientService, private customerService: CommonCustomerService, private configService: ConfigService,
        private notificationService: NotificationClientService, private userClaimsService: UserClaimsService, private mepLookupService: MepLookupService,
        private portDashboard: MepDashboard, private portInsights: MepInsights, private portVessels: MepVessels, private requestClientService: RequestClientService,
        private portYoyComparison: MepYearOverYearComparison, private portEmissionTargets: MepEmissionTargets, private bindingSignaler: BindingSignaler,
        private portAbout: MepAbout, private portsSummary: MepPortsSummary, private portTerminology: MepTerminology, private router: Router, private eventAggregator: EventAggregator,
        private scenarioPlanning: MepScenarioPlanning, private dialogClientService: DialogClientService, private signalClientService: SignalClientService) {
    }

    async activate(params) {
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        if (this.userClientService.currentUser?.businessUnit?.isMepTrialCustomer == true &&
            moment(this.userClientService.currentUser?.businessUnit?.customerMepTrialExpiryDate).diff(moment(), 'days') <= 0) {
            this.isMepTrialExpired = true;
        }
        this.canDownloadEmissionsData = this.userClaimsService.hasPermission(Claims.CanDownloadEmissionsData);

        if (this.isRightShipUser && !this.userClientService.currentUser.businessUnit?.customerId) {
            await this.loadCustomers();
        }
        else {
            await this.loadCustomerId();
        }

        if (!this.isMepTrialExpired) {
            await this.loadCustomerPorts();
            this.tabs = this.getSteps();
            if (params && params.step) {
                this.initialStep = params.step;
            }
        }
    }

    attached() {
        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];

        this._subscriptions.push(this.eventAggregator.subscribe(MepEventMessages.ChangeMepTab, this.onChangeMepTab));
        this.signalClientService.subscribe(SignalFunctions.exportMepEmissionsDocumentStarted, this.onMepEmissionsDocumentStarted);
        this.signalClientService.subscribe(SignalFunctions.exportMepEmissionsDocumentReady, this.onMepEmissionsDocumentReady);

        if (this.initialStep)
            this.setCurrentStep(this.initialStep);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.exportMepEmissionsDocumentStarted, this.onMepEmissionsDocumentStarted);
        this.signalClientService.unsubscribe(SignalFunctions.exportMepEmissionsDocumentReady, this.onMepEmissionsDocumentReady);
        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];
    }

    @computedFrom("years", "years.length", "entityItem", "entityItem.currentStep", "entityItem.currentStep.step")
    get showYearFilter() {
        if (this.entityItem && this.entityItem.currentStep) {
            const excludedTabs = [MepTabsEnum.Home, MepTabsEnum.YoyComparison, MepTabsEnum.EmissionTargets];
            return this.years && this.years.length > 0 && excludedTabs.filter(step => step == this.entityItem.currentStep.step).length == 0;
        }

        return false;
    }

    onMepEmissionsDocumentStarted = (data: { userId: number }) => {
        if (this.userClientService.currentUser.user.userId == data.userId) {
            this._generatingData = true;
        }
    }

    onMepEmissionsDocumentReady = (data: { userId: number }) => {
        if (this.userClientService.currentUser.user.userId == data.userId) {
            this._generatingData = false;
        }
    }

    portIdChanged() {
        if (this.selectedPort) {
            //if current step is not visible set first visible step as current;
            const isCurrentStepVisible = this.entityItem && this.entityItem.currentStep && this.getShow(this.entityItem.currentStep);
            if (!this.selectedPort.hasMultipleFyYears && !isCurrentStepVisible) {
                this.setCurrentStep(null);
            }
            this.mepLookupService.getLookups({
                portId: this.selectedPort.portId,
                years: null,
                queries: [Lookups.FinancialYears],
                emissionTypesIncludeAllYears: false
            }).then(response => {
                this.years = response.allValuesByQuery[Lookups.FinancialYears];
                if (this.years.length > 0) {
                    setTimeout(() => {
                        this.selectedYear = this.years && this.years.filter(x => x.isDefault).length > 0 ? this.years.filter(x => x.isDefault)[0].id : null;
                        this.refreshTabs();
                        this.bindingSignaler.signal('update-tabs');
                    });
                }
                else {
                    this.bindingSignaler.signal('update-tabs');
                }
            }).catch(() => { });
        } else {
            this.years = [];
            this.selectedYear = null;
            this.selectedMonths = null;
            this.refreshTabs();
            this.bindingSignaler.signal('update-tabs');
        }
    }

    customerIdChanged() {
        if (this._isSettingCustomerOnLoad)
            this._isSettingCustomerOnLoad = false;
        else {
            this._isCustomerChange = true;
            this.selectedYear = null;
            this.selectedMonths = null;
            if (this.filterViewModel) {
                this.filterViewModel.selectedYear = null;
                this.filterViewModel.selectedMonths = null;
            }
            // refresh
            this.loadCustomerPorts();
        }
    }

    setCurrentStep(routeStep: string) {
        let step: MepTabModel = null;
        const steps = this.tabs.filter(s => this.getShow(s));
        if (routeStep != undefined) {
            step = steps.filter(s => s.param == routeStep)[0];
        }

        if (!step) {
            step = steps[0];
        }

        if (this.entityItem) {
            this.entityItem.currentStep = step;
        }

        this.setShowPorts(step);
    }

    getShow(s: MepTabModel) {
        return "show" in s ? (typeof (s.show) == "function" ? s.show() : s.show) : true;
    }

    async loadCustomerId() {
        let customerId = this.userClientService.currentUser.businessUnit?.customerId;
        //fallback for getting customerid
        if (customerId == null || customerId <= 0) {
            try {
                customerId = await this.customerService.getCustomerId();
            } catch (e) {
                console.warn(e);
            }
        }

        this._isSettingCustomerOnLoad = true;
        this.customerId = customerId ? customerId : null;
    }

    private async loadCustomers() {
        try {
            this.customers = await this.mepService.getMepCustomers();
            if (this.customers && this.customers.length >= 1) {
                this._isSettingCustomerOnLoad = true;
                this.customerId = this.customers[0].customerPlatformId;
            }
        } catch (e) {
            this.notificationService.show("MEP Error", "Error retrieving Customers", NotificationType.Error);
        }
    }

    private async loadCustomerPorts() {
        try {
            this.ports = await this.mepService.getMepPorts(this.customerId);
            if (this.ports && this.ports.length >= 1) {
                this.portId = this.ports[0].portId;
            }
            else {
                this.portId = null;
            }
        } catch (e) {
            this.notificationService.show("MEP Error", "Error retrieving Ports", NotificationType.Error);
            this.portId = null;
        }
    }

    getTabModel = () => {
        const model = this._changeTabModel ? this._changeTabModel : {};
        model.portId = model.portId ? model.portId : (this.selectedPort ? this.selectedPort.portId : null);
        model.year = model.year ? model.year : (this.selectedYear ? this.selectedYear : null);
        model.months = model.months ? model.months : (this.selectedMonths ? this.selectedMonths : null);
        model.portName = this.selectedPort ? this.selectedPort.portName : null;
        model.portUnlocode = this.selectedPort ? this.selectedPort.portUnlocode : null;
        model.showPortName = this.selectedPort ? this.selectedPort.showPortName : null;
        model.portImageUrl = this.selectedPort ? this.selectedPort.portImageUrl : null;
        model.supportsCargoType = this.selectedPort ? this.selectedPort.supportsCargoType : null;
        model.customerId = this.customerId ? this.customerId : null;
        model.ports = this.ports ? this.ports : null;

        return model;
    }

    getSteps = (): MepTabModel[] => {
        const getModel = () => {
            const model = this.getTabModel();

            //reset change tab model, no longer needed after tab is changed
            this._changeTabModel = null;

            return model;
        };

        const steps: MepTabModel[] = [{
            title: 'Home',
            step: MepTabsEnum.Home,
            show: this.userClaimsService.hasPermission(Claims.CanViewPortHome),
            lazyLoad: true,
            param: MepTabsConstants.Home,
            showPorts: false,
        }, {
            title: 'Dashboard',
            step: MepTabsEnum.Dashboard,
            show: this.userClaimsService.hasPermission(Claims.CanViewPortDashboard),
            vm: this.portDashboard,
            getModel: getModel,
            lazyLoad: true,
            param: MepTabsConstants.Dashboard,
            showPorts: true
        }, {
            title: 'Insights',
            step: MepTabsEnum.Insights,
            show: this.userClaimsService.hasPermission(Claims.CanViewPortInsights),
            vm: this.portInsights,
            getModel: getModel,
            lazyLoad: true,
            param: MepTabsConstants.Insights,
            showPorts: true
        }, {
            title: 'Vessels',
            step: MepTabsEnum.Vessels,
            show: this.userClaimsService.hasPermission(Claims.CanViewPortVessels),
            vm: this.portVessels,
            getModel: getModel,
            lazyLoad: true,
            param: MepTabsConstants.Vessels,
            showPorts: true
        }, {
            title: 'YOY Comparison',
            step: MepTabsEnum.YoyComparison,
            show: () => this.userClaimsService.hasPermission(Claims.CanViewYearOnYear) && this.selectedPort && this.selectedPort.hasMultipleFyYears,
            vm: this.portYoyComparison,
            getModel: getModel,
            lazyLoad: true,
            param: MepTabsConstants.YoyComparison,
            showPorts: true
        }, {
            title: 'Emission Targets',
            step: MepTabsEnum.EmissionTargets,
            show: this.userClaimsService.hasPermission(Claims.CanManageEmissionTargets),
            vm: this.portEmissionTargets,
            getModel: getModel,
            lazyLoad: true,
            param: MepTabsConstants.EmissionTargets,
            showPorts: true
        }, {
            title: 'Scenario Planning',
            step: MepTabsEnum.ScenarioPlanning,
            show: this.userClaimsService.hasPermission(Claims.CanViewScenarioPlanning),
            vm: this.scenarioPlanning,
            getModel: getModel,
            lazyLoad: true,
            param: MepTabsConstants.ScenarioPlanning,
            showPorts: true
        }];

        steps[0].children = [
            {
                title: 'Overview',
                step: MepHomeTabsEnum.PortsSummary,
                show: () => this.ports?.length > 1,
                vm: this.portsSummary,
                getModel: getModel,
                parent: steps[0]
            },
            {
                title: 'About',
                step: MepHomeTabsEnum.About,
                show: this.userClaimsService.hasPermission(Claims.CanViewPortAbout),
                vm: this.portAbout,
                getModel: getModel,
                parent: steps[0]
            },
            {
                title: 'Terminology',
                step: MepHomeTabsEnum.Terminology,
                show: this.userClaimsService.hasPermission(Claims.CanViewPortTerminology),
                vm: this.portTerminology,
                getModel: getModel,
                parent: steps[0]
            }
        ];

        return steps;
    }

    refreshTabs() {
        if (this.currentTab?.vm?.refresh) {
            this.currentTab.vm.refresh({
                portId: this.selectedPort ? this.selectedPort.portId : null,
                year: this.selectedYear ? this.selectedYear : null,
                months: this.selectedMonths ? this.selectedMonths : null,
                portName: this.selectedPort ? this.selectedPort.portName : null,
                portUnlocode: this.selectedPort ? this.selectedPort.portUnlocode : null,
                showPortName: this.selectedPort ? this.selectedPort.showPortName : null,
                portImageUrl: this.selectedPort ? this.selectedPort.portImageUrl : null,
                supportsCargoType: this.selectedPort ? this.selectedPort.supportsCargoType : null,
                customerId: this.customerId ? this.customerId : null
            });
        }
    }

    private getFilterViewModel(tabVM: any) {
        if (tabVM instanceof BaseMepFilterablePage || tabVM instanceof BaseMepFilterableCustomGrdPage) {
            const model = this.getTabModel();

            const filterVM = tabVM.initializeFilterVM(model);
            if (this.selectedYear) {
                filterVM.selectedYear = this.selectedYear;
            }
            if (this.selectedMonths) {
                filterVM.selectedMonths = this.selectedMonths;
            }

            return filterVM;
        }

        return null;
    }

    selectedYearChanged() {
        if (this._isCustomerChange) {
            this._isCustomerChange = false;
            return;
        }

        if (this.compMonthPickerVm) {
            this.compMonthPickerVm.isMonthRangeShowing = true;
        }

        if (!this.selectedPort || !this.selectedYear) {
            return;
        }

        this.mepService.getMepMonthsList(this.selectedPort.portId, this.selectedYear).then(response => {
            const months: MonthYearType[] = [];
            response.items.forEach((item) => {
                months.push({
                    id: item.monthNo,
                    month: item.calendarMonthNo,
                    year: item.year,
                    isSelected: true
                });
            });
            this.months = months;
            this.selectedMonths = null;
            if (this.filterViewModel) {
                this.filterViewModel.selectedYear = this.selectedYear;
                this.filterViewModel.selectedMonths = this.selectedMonths;
                this.filterViewModel.onSelectedMonthsChanged();
            }
        });
    }

    beforeTabChange = (currentStep: MepTabModel, nextStep: MepTabModel) => {
        if (nextStep != null) {
            this.currentTab = nextStep;
            if (this.currentTab) {
                this.filterViewModel = this.getFilterViewModel(this.currentTab.vm);
            }
        }
    }

    public onStepClicked = (step: MepTabModel) => {
        if (!step) {
            return;
        }

        if (step.param) {
            const params = this.router.currentInstruction.params;
            params.step = step.param;

            this.router.navigateToRoute(this.router.currentInstruction.config.name, params, {
                trigger: false,
                replace: true,
            });
        }

        this.setShowPorts(step);
    }

    setShowPorts(step: MepTabModel) {
        this.showPorts = step?.parent?.showPorts ?? step?.showPorts ?? true;
    }

    onChangeMepTab = (model) => {
        if (model && model.step) {

            const tabs = this.tabs.filter(s => this.getShow(s));
            const filteredTabs = tabs.filter(x => x.step == model.step);
            this._changeTabModel = model;
            if (this.entityItem) {
                this.entityItem.currentStep = filteredTabs.length > 0 ? filteredTabs[0] : tabs[0];
            }
        }
    }

    monthsSelectedChanged() {
        this.selectedMonths = this.months.every(x => x.isSelected) ? null : this.months.filter(x => x.isSelected);
        this.filterViewModel.selectedMonths = this.selectedMonths;
        this.filterViewModel.onSelectedMonthsChanged();
    }

    public openDownloadEmissionsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DownloadEmissionsDataDialog, {
            ports: this.ports,
            years: this.years,
            generatingData: this._generatingData
        }, true, false, false, 'right');
    }
    

    //Contact sales
    public contactSales() {
        if(!this.contactSalesEmailSent) {

            //Send request to contact sales
            this.requestClientService.get<number>(
                `${this.configService.config.urls.rightShipPlatformApiUrl}/api/v1/Customer/SendBuyFullVersionRequestByGuid?guid=` + 
                this.userClientService.currentUser.user.guid, {}).then(()=> {
                    this.contactSalesEmailSent = true;
                    this.showDialog = true;
                }).catch((error) => {
                    console.error(error);
                });
        }
    }
}